import React, { useCallback, useEffect, useState } from "react";
import { View } from "react-native";
import { KeyboardAwareScrollView } from "react-native-keyboard-controller";
import FormField from "./FormField";
import { Button } from "react-native-paper";
import { createZodSchema, getNestedValue } from "../utils/utils";
import { z } from "zod";
import { useAppSelector } from "../redux/store";
import { useTranslation } from "react-i18next";
import { uploadFiles } from "../services/storage";
import { User } from "../redux/slices/authSlice";

import { Field } from "../config/companies/config";

interface FormData {
  [key: string]: any;
}

const buildFormData = (fields: Field[], existingData?: FormData): FormData => {
  const defaults = fields.reduce((acc, field) => {
    const isDateField =
      field.type === "datePicker" || field.type === "dateTime";
    const defaultValue = isDateField ? new Date() : field.defaultValue;

    if (field.type === "booleanCheckbox") {
      return { ...acc, [field.name]: field.defaultValue ?? false };
    }
    if (field.type === "testPicker" || field.type === "mechanicPicker") {
      return { ...acc, [field.name]: [] };
    }
    if (field.type === "warehouseItemsPicker") {
      return { ...acc, [field.name]: {} };
    }

    return { ...acc, [field.name]: defaultValue ?? "" };
  }, {} as FormData);

  return existingData ? { ...defaults, ...existingData } : defaults;
};

const DynamicForm = ({
  fields,
  handleSubmit,
  existingData,
  collectionName,
  collection,
}: {
  fields: Field[];
  handleSubmit: (data: FormData) => void;
  existingData?: FormData;
  collectionName: string;
  collection: any;
}) => {
  const { role, company } = useAppSelector((state) => state.auth.user);
  const [formData, setFormData] = useState<FormData>(
    buildFormData(fields, existingData),
  );

  console.log("FORM DATA", formData);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const user = useAppSelector((state) => state.auth.user);

  const fileField = fields.find(
    (field) =>
      field.type === "file" ||
      field.type === "fileAndDocument" ||
      field.type === "document",
  );

  const schema = createZodSchema(fields);
  const { t } = useTranslation();

  useEffect(() => {
    if (existingData) {
      setFormData(buildFormData(fields, existingData));
    }

    return () => {
      setFormData(
        fields.reduce(
          (acc: FormData, field: Field) => ({ ...acc, [field.name]: "" }),
          {},
        ),
      );
    };
  }, [existingData, fields]);

  useEffect(() => {
    fields.forEach((field) => {
      const fieldValue = formData[field.name];
      const hasFieldValue =
        Array.isArray(fieldValue) || typeof fieldValue === "object"
          ? Boolean(fieldValue && Object.keys(fieldValue).length)
          : fieldValue !== undefined &&
            fieldValue !== null &&
            fieldValue !== "";

      if (field.useUserValue && (user as any)[field.name] && !hasFieldValue) {
        setFormData((prev) => ({
          ...prev,
          [field.name]: (user as any)[field.name],
        }));
      }
    });
  }, [fields, formData, user]);

  const handleFieldChange = useCallback(
    (name: string, value: any) => {
      setFormData((data) => {
        const nextData = { ...data, [name]: value };

        fields.forEach((field) => {
          if (field.type === "dropdown" || !field.autoSelectFrom) {
            return;
          }

          const [sourceField] = field.autoSelectFrom.split(".");
          if (sourceField !== name) {
            return;
          }

          nextData[field.name] =
            getNestedValue(nextData, field.autoSelectFrom) ?? "";
        });

        fields.forEach((field) => {
          if (field.type !== "collectionPicker" || !field.where) {
            return;
          }

          const dependsOnChangedField = field.where.some(
            (condition) =>
              condition.from === "formData" &&
              typeof condition.value === "string" &&
              (condition.value === name ||
                condition.value.startsWith(`${name}.`)),
          );

          if (!dependsOnChangedField || field.name === name) {
            return;
          }

          nextData[field.name] = field.multiselect ? [] : "";
        });

        return nextData;
      });
    },
    [fields],
  );

  useEffect(() => {
    if (collection?.defaultFields) {
      collection.defaultFields?.forEach(
        (item: { name: string; key: string }) => {
          if (user[item.key as keyof User]) {
            formData[item.name] = user[item.key as keyof User];
          }
        },
      );
    }
  }, [collection?.defaultFields, formData, user]);

  const onSubmit = async () => {
    try {
      setLoading(true);
      const validatedData = await schema.parseAsync(formData);

      if (fileField) {
        const files = formData[fileField.name];
        validatedData[fileField.name] = await uploadFiles(
          files,
          company?.id || "",
        );
        console.log("new fields file", validatedData[fileField.name]);
      }

      await handleSubmit(validatedData);
    } catch (error) {
      if (error instanceof z.ZodError) {
        const newErrors: { [key: string]: string } = {};
        error.issues.forEach((err) => {
          if (err.path && err.path[0]) {
            newErrors[err.path[0] as string] = err.message;
          }
        });
        setErrors(newErrors);
      } else {
        alert((error as any)?.message || t("An error occurred"));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <KeyboardAwareScrollView
      contentContainerStyle={{ paddingBottom: 30 }}
      bottomOffset={20}
      keyboardShouldPersistTaps="handled"
    >
      <View>
        {fields
          .filter((field) => {
            if (field.type === "numberId") {
              return false;
            }

            if (
              field.visibleWhen &&
              ((field.visibleWhen.notEmpty &&
                !Boolean(formData[field.visibleWhen.field])) ||
                (!field.visibleWhen.notEmpty &&
                  formData[field.visibleWhen.field] !==
                    field.visibleWhen.equals))
            ) {
              return false;
            }

            if (existingData) {
              // If editing existing data, check formEdit
              const roleArray = field.formEdit || field.form || [];
              return roleArray.includes(role);
            } else {
              // If creating new data, check formCreate
              const roleArray = field.formCreate || field.form || [];
              return roleArray.includes(role);
            }
          })
          .map((field, index) => (
            <View key={field.name} style={{ marginBottom: 10 }}>
              <FormField
                formData={formData}
                index={index}
                field={field}
                errors={errors}
                value={formData[field.name]}
                onChange={handleFieldChange}
                collection={collectionName}
              />
            </View>
          ))}
      </View>
      <Button
        testID={"submit"}
        mode="contained"
        loading={loading}
        disabled={loading}
        onPress={onSubmit}
      >
        {t("Submit2")}
      </Button>
    </KeyboardAwareScrollView>
  );
};

export default DynamicForm;
